import { Inject, Injectable } from '@angular/core';
import { LocaleUtil } from '@ddt-web/common';
import { APPLICATION_NAME_TOKEN, PortfolioRetrievalParamsConfig } from '@ddt-web/config';
import { InjectConfig, LocaleUtilResolverService, SharedConfigKey } from '@ddt-web/config-layer';
import { PortfolioRetrievalParams } from '@ddt-web/domain';
import { LocalStorageKey } from '@ddt-web/generic-utils';
import { UrlUtils } from '@ddt-web/url';
import { UserDataService, UserProfile } from '@ddt-web/user-data';
import { Observable, of } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { PasDataServiceApiConstants } from './constants/pas-data-service-api.constants';
import { PasFeature } from './enums';

/**
 * The PasRcResolverService should always be used when deciding which rc to pass to a PAS API. It builds the rc based on the PasDataServiceApiConstants.PAS_RCS constants,
 * but allows us to provide rc overrides based on the user profile.
 * 
 * If an override is provided, a new directory should be created in TeamSite for the corresponding rc overrides.
 * e.g. if there is an override for Swiss users that points to `ch`, pc:security-details will become pc:ch:security-details
 */
@Injectable({ providedIn: 'root' })
export class PasRcResolverService {
	@InjectConfig(SharedConfigKey.PAS_RC_TENANT_OVERRIDES) private readonly tenantOverrides: { [userType: string]: string } = {};

	private readonly US_REGION = 'us';

	constructor(
		@Inject(APPLICATION_NAME_TOKEN) private appName: string, private userDataService: UserDataService,
		private localeUtilResolverService: LocaleUtilResolverService
	) { }

	public getRcForUserType(rcType: string, locale?: string): Observable<string> {
		const mockCountry = localStorage.getItem(LocalStorageKey.MockCountry);
		const rcTypeWithLocale = locale && locale !== this.US_REGION ? `${locale}-${rcType}`
			: rcType;
		const rc: string = PasDataServiceApiConstants.PAS_RCS[this.appName][rcTypeWithLocale];

		return this.userDataService.getUserProfile().pipe(
			take(1),
			map((userProfile: UserProfile) => {
				let userType: string = userProfile?.internalUser
					? 'internal'
					: (userProfile?.userExtendedAttributes?.crmPrimaryCountryCode ?? userProfile?.userExtendedAttributes?.PRIMARY_COUNTRY_CODE)?.toLowerCase();
				if (!UrlUtils.isProdDmz() && userType === 'internal' && Boolean(mockCountry)) {
					userType = LocaleUtil.getCountryStringFromLocaleExpr(mockCountry);
				}

				if (!userType) {
					userType = this.localeUtilResolverService.getResolvedLocaleValue();
				}

				let resolvedRc = rc;

				if (!!this.tenantOverrides[userType]) {
					const rcParts: string[] = rc.split(':');
					rcParts.splice(rcParts.length - 1, 0, this.tenantOverrides[userType]);
					resolvedRc = rcParts.join(':');
				}
				return resolvedRc;
			})
		);
	}

	public getPortfolioRetrievalParams(): Observable<PortfolioRetrievalParams> {
		if (PortfolioRetrievalParamsConfig.SKIP_SECURITY_ENRICHMENT_APPS.includes(this.appName)) {
			return this.getRcForUserType(PasDataServiceApiConstants.SECURITY_DETAILS).pipe(
				map((rc: string) => ({
					skipSecurityEnrichment: true,
					securityDetailsRc: rc
				}))
			);
		} else {
			console.warn('Backend api params are not configured. Using default. Please configure.');
			return of(PortfolioRetrievalParamsConfig.DEFAULT);
		}
	}

	public getRcForPasFeature(rc: string, feature?: PasFeature): string {
		if (feature === PasFeature.COMPARE) {
			return PasDataServiceApiConstants.SECURITY_DETAILS_COMPARE;
		} else {
			return rc;
		}
	}
}
